import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { useSnackbar } from "notistack";
import { createDateAcceptHandler, parseDateValue } from "utility/dateHelpers";
import { formatPrice } from "utility/formatters";

const normalizeRepairData = (repairData) => {
  if (repairData) {
    return {
      ...repairData,
      parts: repairData.parts || "",
      labor: repairData.labor || "",
    };
  }
  return {
    repair_date: null,
    description: "",
    vendor: "",
    parts: "",
    labor: "",
    is_active: true,
  };
};

export default function EquipmentInventoryRepairEditDialog({ onClose, repair, onChange }) {
  const { enqueueSnackbar } = useSnackbar();

  const [state, setState] = useState(() => normalizeRepairData(repair));
  const [formErrors, setFormErrors] = useState({});
  const formMaxWidthShort = "1200px";

  useEffect(() => {
    setState(normalizeRepairData(repair));
  }, [repair]);

  const total = useMemo(() => {
    const parts = Number(state?.parts) || 0;
    const labor = Number(state?.labor) || 0;
    return parts + labor;
  }, [state?.parts, state?.labor]);

  const handleChangeState = (event, value) => {
    const eventValue = event.target.value;
    const eventName = event.target.name;
    const newValue = value ?? eventValue;

    if (formErrors[eventName]) {
      setFormErrors((prev) => ({ ...prev, [eventName]: undefined }));
    }

    setState((prev) => ({
      ...(prev || {}),
      [eventName]: newValue,
    }));
  };

  const handleDateAccept = useCallback(
    createDateAcceptHandler({
      onChange: handleChangeState,
      onError: (message) => {
        enqueueSnackbar(message, {
          variant: "error",
          preventDuplicate: true,
        });
        setFormErrors((prev) => ({ ...prev, repair_date: "Invalid date" }));
      },
      format: "YYYY-MM-DD",
      fieldName: "repair_date",
      clearErrors: (field) => {
        if (formErrors[field]) {
          setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    }),
    [enqueueSnackbar, formErrors, handleChangeState]
  );

  const validateForm = () => {
    const errors = {};
    const fields = [];

    if (!state?.repair_date) {
      errors.repair_date = "Required";
      fields.push("Date");
    }

    if (!state?.description?.trim()) {
      errors.description = "Required";
      fields.push("Description");
    }

    setFormErrors(errors);
    return fields;
  };

  const handleSave = () => {
    const fields = validateForm();
    if (fields.length > 0) {
      enqueueSnackbar(`Please correct the following fields: ${fields.join(", ")}`, { variant: "error" });
      return;
    }

    if (!state) {
      enqueueSnackbar("Form state is invalid", { variant: "error" });
      return;
    }

    const repairData = {
      ...state,
      repair_date: state.repair_date || null,
    };

    onChange(repairData);
    onClose();
  };

  return (
    <>
      <DialogTitle>{repair?.id ? "Edit Repair" : "Add Repair"}</DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={!!formErrors.repair_date}>
                <FormLabel>Date</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name="repair_date"
                    format="MM/DD/YYYY"
                    value={parseDateValue(state?.repair_date)}
                    onChange={handleDateAccept}
                    slots={{
                      textField: TextField,
                    }}
                    slotProps={{
                      textField: {
                        name: "repair_date",
                        fullWidth: true,
                        InputLabelProps: { shrink: true },
                        error: !!formErrors.repair_date,
                        helperText: formErrors.repair_date || " ",
                        onKeyDown: (e) => {
                          if (e.key === "Tab") {
                            e.stopPropagation();
                          }
                        },
                      },
                      openPickerButton: {
                        tabIndex: -1,
                      },
                      field: {
                        clearable: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Description"
                  name="description"
                  isRequired
                  defaultValue={state?.description || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  errorText={formErrors.description}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Vendor"
                  name="vendor"
                  defaultValue={state?.vendor || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Parts"
                  name="parts"
                  defaultValue={state?.parts || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Labor"
                  name="labor"
                  defaultValue={state?.labor || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <TextField
                  label="Total"
                  name="total"
                  value={formatPrice(total, 0)}
                  InputLabelProps={{ shrink: true }}
                  disabled
                  fullWidth
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
          <Button variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}
